import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import AlertDialog from '../popupComp/AlertDialog';
import InputGroup from '../inputComp/InputGroup';
import { IdIcon } from '../iconSet/IconSet';
import { SelectItemTypes } from '../../CommonTypes';
import UserTable from './UserTable';
import RoleTable from './RoleTable';
import { APIRequestGetIncidentInform, APIRequestGetIncidentPrivilege, APIRequestUpdateIncidentInform, IncidentInformDataType, RequestIncidentInfo, ResponseUserRole, UserData } from './IncidentManageService';
import LoadingScreen from '../loadingScreenComp/LoadingScreen';

const IncidentManage = () => {

    const showAlertDialog = (
        title: string,
        description: string,
        yesCallback?: () => void,
        okCallback?: () => void
        ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    };

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [isHazardTypeSelected, setIsHazardTypeSelected] = useState<boolean>(false)
    const hazardTypes: SelectItemTypes[] = [
        { name: 'Wild Fire', value: 'IFFIR' },
        { name: 'Fire', value: 'IFIR' },
        { name: 'Flood', value: 'IFLW' },
        // { name: 'Landslide symptoms', value: 'ILSS' },
        // { name: 'Landslide', value: 'ILSW' },
        { name: 'Cyclone', value: 'ICYC' },
        { name: 'Tsunami', value: 'ITSM' },
        { name: 'Thunderstorm', value: 'ITHS' },
        { name: 'Heavy Rain', value: 'IHRW' },
        { name: 'Earthquake', value: 'IETQ' },
        { name: 'Explosion', value: 'EXPL' },
        { name: 'Nuclear radioactive', value: 'NUCL' },
        { name: 'Biological hazard', value: 'BIOL' }
    ]
    const hazardTypeRef = useRef<HTMLSelectElement | null>(null)

    const handleChangeHazardValue = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value
        value === '' ? setIsHazardTypeSelected(false) : setIsHazardTypeSelected(true)
        setIsTypeChanged(prevState => !prevState)
    }

    const [allUsers, setAllUsers] = useState<UserData[]>([])
    const [selectedUsers, setSelectedUsers] = useState<UserData[]>([])

    const [allRole, setAllRole] = useState<ResponseUserRole[]>([])
    const [selectedRoles, setSelectedRoles] = useState<ResponseUserRole[]>([])

    const [isTypeChanged, setIsTypeChanged] = useState<boolean>(false)

    const roleModeRef = useRef<HTMLInputElement | null>(null)
    const userModeRef = useRef<HTMLInputElement | null>(null)

    const [isTableRoleMode, setIsTableRoleMode] = useState<boolean>(true)
    const [isTableUserMode, setIsTableUserMode] = useState<boolean>(false)

    const handleSaveClick = useCallback(async () => {
        if (!hazardTypeRef.current || !roleModeRef.current || !userModeRef.current) return
    
        setIsLoading(true)
    
        const body: IncidentInformDataType = {
            incidentType: hazardTypeRef.current.value,
            usernames: selectedUsers.map(item => item.username),
            roles: selectedRoles.map(item => item.roleid),
            userSpecific: userModeRef.current.checked,
            roleSpecific: roleModeRef.current.checked
        }

        //console.log('log ->', body)
    
        try {
            const response = await APIRequestUpdateIncidentInform(body)
            setIsLoading(false)
    
            if (response.status) {
                showAlertDialog(
                    "success",
                    "✅ Data submitted successfully",
                    undefined,
                    () => window.location.reload()
                )
                return
            }
    
            showAlertDialog("error", "❌ Data submission failed")
        } catch (error) {
            console.error(error)
            setIsLoading(false) // ✅ important: ensure loading resets on error
            showAlertDialog("error", "❌ Data submission failed")
        }
    }, [isTableRoleMode, isTableUserMode, selectedRoles, selectedUsers, APIRequestUpdateIncidentInform])

    useEffect(() => {
        if (!isHazardTypeSelected) return

        const getInfo = async () => {
            if (!hazardTypeRef.current) return
            setIsLoading(true)

            const body: RequestIncidentInfo = {
                type: hazardTypeRef.current.value
            }
            
            const response = await APIRequestGetIncidentInform(body)
            if (response.status)
            {
                response.data?.usernames.map(item => {
                    const user: UserData[] = allUsers.filter(user => user.username === item)
                    setSelectedUsers(prevState => [...prevState, ...user])
                })

                response.data?.roles.map(item => {
                    const role: ResponseUserRole[] = allRole.filter(user => user.roleid === item)
                    setSelectedRoles(prevState => [...prevState, ...role])
                })
            }
            setIsLoading(false)
        }

        getInfo()
    }, [isHazardTypeSelected, isTypeChanged, allUsers, allRole])

    const handleClickRoleMode = (event: ChangeEvent<HTMLInputElement>) => {
        const value: boolean = event.target.checked
        value ? setIsTableRoleMode(true) : setIsTableRoleMode(false)
    }

    const handleClickUserMode = (event: ChangeEvent<HTMLInputElement>) => {
        const value: boolean = event.target.checked
        value ? setIsTableUserMode(true) : setIsTableUserMode(false)
    }

    useEffect(() => {
        if (!isHazardTypeSelected || !roleModeRef.current || !userModeRef.current) return

        const getInfo = async () => {
            if (!hazardTypeRef.current) return
            setIsLoading(true)

            const body: RequestIncidentInfo = {
                type: hazardTypeRef.current.value
            }
            
            const response = await APIRequestGetIncidentPrivilege(body)
            if (response.status)
            {
                if (roleModeRef.current) {
                    const value = response.data ? response.data.roleSpecific : true
                    roleModeRef.current.checked = value
                    value ? setIsTableRoleMode(true) : setIsTableRoleMode(false)
                }
                
                if (userModeRef.current) {
                    const value = response.data ? response.data.userSpecific : false
                    userModeRef.current.checked = value
                    value ? setIsTableUserMode(true) : setIsTableUserMode(false)
                } 
            }
            setIsLoading(false)
        }
        getInfo()
    }, [isTypeChanged])

    return (
        <div className={`container-wrapper`} style={{ height: "100%" }}>
            <span className={`title`}>Incident Approval Mapping</span>

            {/* Incident */}
            <div className={`incident-approval-mapping-wrapper`}>
                <div className='section type-select'>
                    <span className='section-title'>Select Hazard Type</span>
                    <InputGroup type='select' icon={<IdIcon width="1.5rem" color="var(--color-main-white)" />} color='var(--color-accent-bg)' placeholder='Select hazard type' label='Hazard Type' name='hazard_type' required={true} componentSelectElement={hazardTypeRef} optionList={hazardTypes} onChange={(e) => handleChangeHazardValue(e)}/>
                </div>
                {isHazardTypeSelected && (<div className='section table-type-select'>
                    <span className='section-title'>Incident management privileges</span>
                    <div className='checkbox-wrapper'>
                        <div className='cell'>
                            <input ref={roleModeRef} type="checkbox" id='checkbox-role-mode' onChange={(e) => handleClickRoleMode(e)}/>
                            <label htmlFor="checkbox-role-mode">Role specific</label>
                        </div>
                        <div className='cell'>
                            <input ref={userModeRef} type="checkbox" id='checkbox-user-mode' onChange={(e) => handleClickUserMode(e)}/>
                            <label htmlFor="checkbox-user-mode">User specific</label>
                        </div>
                    </div>
                </div>)}

                {isHazardTypeSelected && (isTableRoleMode || isTableUserMode) && (
                    <div className='section type-select'>
                        <div className='table-wrapper'>
                            {isTableRoleMode && (
                                <>
                                    <RoleTable data={selectedRoles} setData={setSelectedRoles} allData={setAllRole}/>
                                    <span>Note: If the role does not exist in the roles table, create a new role entry before assigning</span>
                                </>
                                
                            )}
                            {isTableUserMode && (
                                <>
                                    <UserTable data={selectedUsers} setData={setSelectedUsers} allData={setAllUsers}/>
                                    <span>Note: If the user does not exist in the user table, create a new user entry before assigning</span>
                                </>
                            )}
                        </div>
                    </div>
                )}

            {isHazardTypeSelected && (
                <div className={`button-wrapper`} style={{ gridArea: 'button' }}>
                    <div className={`left-side`}></div>
                    <div className={`right-side`}>
                        <button className={`button-normal`} style={{ backgroundColor: 'var(--color-main-red)' }}>cancel</button>
                        <button className={`button-normal`} onClick={(e) => 
                            showAlertDialog(
                                "Confirm Submit",
                                "Are you sure you want to submit this data?",
                                () => handleSaveClick(), undefined)
                        }>save</button>
                    </div>
                </div>
            )}
                
            </div>

            {alertDialogState && (
                <AlertDialog
                    title={alertTitle}
                    description={alertDescription}
                    windowState={alertDialogState}
                    setWindowState={setAlertDialogState}
                    function={alertYesCallback}
                    okFunction={alertOkCallback}
                />
            )}
            {isLoading && (
                <LoadingScreen />
            )}
        </div>
    )
}

export default IncidentManage